import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_ROUTES } from '@zenda/api-contract';
import { getToken } from './api';
import { invalidarAcesso, keys } from './queries';
import { avisar } from './aviso';
import { marcarEscrevendo } from './presenca';

/**
 * O que chega pelo stream. O servidor já filtrou pelo que a pessoa enxerga e pelo
 * foco (docs/domain/equipes.md §7.2): o que chega aqui é dela.
 */
type EventoDeTenant =
  | {
      readonly tipo: 'conversa';
      readonly tenantId: string;
      readonly conversationId: string;
      readonly entrada: boolean;
    }
  | { readonly tipo: 'agenda'; readonly clientId: string }
  | { readonly tipo: 'acesso' }
  | {
      readonly tipo: 'presenca';
      readonly conversationId: string;
      readonly userId: string;
    };

/**
 * Espera antes de reconectar, em ms. Cresce e para em 30 s.
 *
 * Sem teto, uma queda longa levaria a espera para horas e a tela nunca voltaria
 * sozinha. Sem crescimento, uma API fora do ar receberia uma tentativa por
 * segundo de cada aba aberta — que é como um incidente pequeno vira grande.
 */
const ESPERAS = [1_000, 2_000, 5_000, 10_000, 30_000];

const JANELA_DE_EVENTOS_MS = 300;

/**
 * Sem nenhum byte por este tempo, a conexão está morta e é refeita.
 *
 * O servidor manda ping a cada 25 s. Proxy que perde o lado do servidor sem fechar o
 * do navegador (o do Vite faz isso quando a API reinicia) deixa a leitura esperando
 * para sempre, sem erro: a aba parece ligada e não recebe mais nada.
 */
const SILENCIO_MS = 60_000;

/**
 * Ouve o que muda no servidor e invalida as queries correspondentes.
 *
 * ── Por que `fetch` com stream, e não `EventSource` ──
 * `EventSource` não manda header, e a API autentica por Bearer. As duas saídas
 * seriam token na URL — que vai parar em log de acesso e `Referer` — ou um
 * sistema de tíquete de uso único. O `fetch` mantém o token no header e custa
 * este laço de reconexão, que é menos código que o tíquete.
 *
 * ── O evento é um EMPURRÃO, não o dado ──
 * Ele diz que a conversa X mudou; quem busca é o TanStack, pela rota autenticada
 * de sempre. Empurrar o conteúdo criaria um segundo lugar onde o isolamento
 * entre tenants precisa estar certo.
 */
export function useStream(ativo: boolean, aoMudarAcesso?: () => void, tenantDaCasa?: string): void {
  const client = useQueryClient();

  useEffect(() => {
    if (!ativo) return;

    const controller = new AbortController();
    let vivo = true;
    let tentativa = 0;

    // Uma rajada de eventos (importacao de historico, varias mensagens seguidas) vira
    // UMA busca da lista e do painel por aba, e nao uma por evento.
    const pendentes = new Set<string>();
    let invalidacao: ReturnType<typeof setTimeout> | null = null;
    const agendarInvalidacao = (conversationId: string): void => {
      pendentes.add(conversationId);
      if (invalidacao !== null) return;
      invalidacao = setTimeout(() => {
        invalidacao = null;
        void client.invalidateQueries({ queryKey: keys.conversations });
        void client.invalidateQueries({ queryKey: keys.stats });
        void client.invalidateQueries({ queryKey: keys.monthlyCost });
        void client.invalidateQueries({ queryKey: ['piloto'] });
        for (const id of pendentes) {
          void client.invalidateQueries({ queryKey: keys.conversation(id) });
        }
        pendentes.clear();
      }, JANELA_DE_EVENTOS_MS);
    };

    async function ligar(): Promise<void> {
      const token = getToken();
      if (token === null) return;

      const conexao = new AbortController();
      const sair = (): void => conexao.abort();
      controller.signal.addEventListener('abort', sair);
      let ultimoByte = Date.now();
      const vigia = setInterval(() => {
        if (Date.now() - ultimoByte > SILENCIO_MS) conexao.abort();
      }, 10_000);
      try {
        await ler(token, conexao.signal, () => {
          ultimoByte = Date.now();
        });
      } finally {
        clearInterval(vigia);
        controller.signal.removeEventListener('abort', sair);
      }
    }

    async function ler(token: string, sinal: AbortSignal, aoReceber: () => void): Promise<void> {
      const r = await fetch(API_ROUTES.stream, {
        headers: { Authorization: `Bearer ${token}` },
        signal: sinal,
      });
      // Um 401 ou 503 tambem tem corpo: sem esta recusa ele contaria como conexao
      // bem-sucedida, zeraria a espera e a aba tentaria de novo a cada segundo.
      if (!r.ok || r.body === null) throw new Error(`stream recusado: ${r.status}`);

      tentativa = 0;
      const leitor = r.body.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = '';

      for (;;) {
        const { value, done } = await leitor.read();
        if (done) break;
        aoReceber();
        buffer += value;

        // Um evento SSE termina em linha em branco. O `while` existe porque um
        // único `read` pode trazer vários — ou meio.
        let corte = buffer.indexOf('\n\n');
        while (corte !== -1) {
          const bloco = buffer.slice(0, corte);
          buffer = buffer.slice(corte + 2);
          corte = buffer.indexOf('\n\n');

          // `:` é comentário no protocolo — é o ping que segura a conexão.
          const linha = bloco.split('\n').find((l) => l.startsWith('data: '));
          if (linha === undefined) continue;

          try {
            const e = JSON.parse(linha.slice(6)) as EventoDeTenant;
            if (e.tipo === 'conversa') {
              agendarInvalidacao(e.conversationId);
              if (e.entrada) void avisar(e.tenantId === tenantDaCasa);
            } else if (e.tipo === 'agenda') {
              // A agenda aberta daquele cliente, e o painel, buscam de novo.
              void client.invalidateQueries({
                predicate: (q) =>
                  q.queryKey.includes(e.clientId) ||
                  q.queryKey[0] === 'appointments' ||
                  q.queryKey[0] === 'stats',
              });
            } else if (e.tipo === 'acesso') {
              invalidarAcesso(client);
              aoMudarAcesso?.();
            } else if (e.tipo === 'presenca') {
              marcarEscrevendo(e.conversationId, e.userId);
            }
          } catch {
            // Bloco malformado não derruba o stream: o próximo vem inteiro.
          }
        }
      }
    }

    void (async () => {
      while (vivo) {
        try {
          await ligar();
        } catch {
          if (!vivo) return;
        }
        if (!vivo) return;
        const espera = ESPERAS[Math.min(tentativa, ESPERAS.length - 1)]!;
        tentativa += 1;
        await new Promise((r) => setTimeout(r, espera));
      }
    })();

    return () => {
      vivo = false;
      controller.abort();
      if (invalidacao !== null) clearTimeout(invalidacao);
    };
  }, [ativo, client, aoMudarAcesso, tenantDaCasa]);
}
